import React, { useState } from "react";
import { GridColumns } from "@mui/x-data-grid";
import { Checkbox, FormControl, Grid, Tooltip, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { MailInboxAddresseeDTO } from "../../models/mail-inbox-addressee/mail-inbox-addressee-dto";
import SimplePopover from "../../reusable-components/SimplePopover/SimplePopover";
import Table from "../../reusable-components/Table/Table";
import AddEditMailInboxAddressee from "../../containers/AddEditMailInboxAddressee/AddEditMailInboxAddressee";
import { FT_MMIA } from "../../utils/constants/filter-tables-constants";
import { MailInboxAddresseesApiService } from "../../services/mail-inbox-addressees/mail-inbox-addressees-api.service";
import { MailInboxQuickfilter, useMailInboxQuickFilter } from "../Quickfilters/MailInboxQuickfilter";
import { MailInboxAddresseeFilter, MailInboxAddresseeRef } from "src/models/mail-inbox-addressee";
import FlagIcon from "../FlagIcon/FlagIcon";
import { MailInboxRef } from "src/models/mail-inbox";
import { useAppSelector } from "src/custom-hooks/redux/redux";
import { AUTHORIZATIONS } from "src/utils/constants/authorization-constants";
import CopyMailInboxAddressee from "./CopyMailInboxAddressee";
const ManageMailInboxAddresseesTable = () => {
  const tRP = "components.mail_inbox_addressees_table";
  const { t } = useTranslation();
  const theme = useTheme();

  const { mailInboxQuickFilter, setMailInboxQuickFilter, selectedMailInbox } = useMailInboxQuickFilter();
  const [displayOnlyActive, setDisplayOnlyActive] = useState<boolean>(true);

  const { currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);
  //#region mail inbox list
  //get the list of mail inboxes this user has access to their incoming mails
  //this will be used to filter the mail inboxes this user can chose from the quickfilter
  const authorizedMailInboxesForLetterAddressees: string[] =
    currentUserAuthorizations?.allowedMailInboxes
      ?.filter(
        (allowedMailInbox) =>
          allowedMailInbox.value?.includes(AUTHORIZATIONS.MailInboxAccess.CanUploadIncoming) ||
          allowedMailInbox.value?.includes(AUTHORIZATIONS.MailInboxAccess.CanUploadOutgoing)
      )
      .map((allowedMailInbox) => allowedMailInbox.key) ?? [];
  //#endregion

  const columns: GridColumns<MailInboxAddresseeDTO> = [
    {
      field: "inbox",
      renderHeader: () => <Typography>{`${t(`${tRP}.fields.mail_inbox`)}`}</Typography>,
      valueGetter: (params) => params.row.mailInbox?.name,
      align: "left",
      headerAlign: "center",
      flex: 1,
    },
    {
      field: "nickname",
      renderHeader: () => <Typography>{`${t(`${tRP}.fields.nickname`)}`}</Typography>,
      headerAlign: "center",
      align: "left",
      flex: 1,
    },
    {
      field: "isActive",
      renderHeader: () => <Typography>{`${t(`common_words.status`)}`}</Typography>,
      headerAlign: "center",
      align: "center",
      valueGetter: ({ row }) => (row.isActive ? `${t(`${tRP}.fields.active`)}` : `${t(`${tRP}.fields.deactivated`)}`),
      flex: 0.7,
    },
    {
      field: "address",
      renderHeader: () => <Typography>{`${t(`${tRP}.fields.address`)}`}</Typography>,
      headerAlign: "center",
      align: "left",
      flex: 2.5,
      renderCell: ({ row }) => (
        <Grid style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          <Grid style={{ overflow: "hidden" }}>
            <Typography variant="body2">{`${row.companyName}`}</Typography>
            <Typography variant="body2">{`${row.contactPersonName}`}</Typography>
            <Typography variant="body2">{`${row.addressLineOne}, ${row.location}, ${row.zip}`}</Typography>
          </Grid>
          {row.country && (
            <Tooltip title={row.country.name}>
              <FlagIcon countryCode={row.country?.code} />
            </Tooltip>
          )}
        </Grid>
      ),
    },
    {
      field: "recipients",
      renderHeader: () => <Typography>{`${t(`${tRP}.fields.addressees`)}`}</Typography>,
      headerAlign: "center",
      align: "left",
      renderCell: ({ row }) => (
        <Grid style={{ overflow: "hidden", display: "flex" }}>
          <Grid style={{ width: row.recipients.length > 3 ? "85%" : "100%" }}>
            {row.recipients.slice(0, 3).map((recipient, idx) => (
              <Typography
                key={idx}
                variant="body2"
              >{`${recipient.user?.name} (${recipient.user?.email})`}</Typography>
            ))}
          </Grid>
          {row.recipients.length > 3 && (
            <SimplePopover
              buttonType="text"
              buttonContent={`+ ${row.recipients.length - 3}`}
            >
              <Grid style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {row.recipients.slice(3).map((recipient, idx) => (
                  <Typography key={idx}>{`${recipient.user?.name} (${recipient.user?.email})`}</Typography>
                ))}
              </Grid>
            </SimplePopover>
          )}
        </Grid>
      ),
      flex: 2,
    },
  ];
  return (
    <>
      <Table
        serviceApi={new MailInboxAddresseesApiService()}
        displayTitle={false}
        translationRootPath={tRP}
        filter={FT_MMIA}
        columns={columns}
        addEditType="custom"
        CustomAddEditComponent={AddEditMailInboxAddressee}
        quickFilters={
          <>
            <MailInboxQuickfilter
              quickFilters={mailInboxQuickFilter}
              handleChangeQuickFilters={setMailInboxQuickFilter}
              customPropsFilter={{ ids: authorizedMailInboxesForLetterAddressees }}
            />
            <Grid
              style={{
                display: "flex",
                alignItems: "center",
                backgroundColor: theme.palette.background.paper,
                padding: " 5px 20px 5px 5px",
                borderRadius: "16px",
              }}
            >
              <Checkbox
                checked={displayOnlyActive}
                onChange={(_, checked) => setDisplayOnlyActive(checked)}
              />
              <Typography>{`${t(`${tRP}.display_only_active`)}`}</Typography>
            </Grid>
          </>
        }
        propsForCustomAddEdit={{
          mailInbox: selectedMailInbox as MailInboxRef,
        }}
        propsFilter={
          {
            mailInboxIds: mailInboxQuickFilter.mailInboxes.mailInboxIds,
            isActive: displayOnlyActive ? true : null,
          } as MailInboxAddresseeFilter
        }
        AdditionalActionsforAddEditColumn={CopyMailInboxAddressee}
      />
    </>
  );
};

export default ManageMailInboxAddresseesTable;
